/**
 * @file     ui.tsx
 * @purpose  Small shared pieces for the owner-listing screens, all on design
 *           tokens as CSS variables so light and dark resolve before
 *           hydration: the 48px action buttons, choice chips (a radio or
 *           checkbox group with 44px targets), the field "why" line, a flat
 *           panel, a status dot and the line icons the screens use.
 */
'use client';

import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button, { type ButtonProps } from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { focusRingSx, hairline, hubColors, microLabelSx, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';

export { hubColors, hairline, microLabelSx, focusRingSx };

export const radius = layoutTokens.radius;

type OwnerButtonProps = Omit<ButtonProps, 'variant' | 'size'> & { tone?: 'primary' | 'secondary' | 'quiet'; size?: 'sm' | 'md' };

/** 48px (md) or 40px (sm) action button at radius 16. One primary per screen. */
export function OwnerButton({ tone = 'primary', size = 'md', sx, children, ...rest }: OwnerButtonProps) {
  const spec = layoutTokens.button[size];
  return (
    <Button
      disableElevation
      variant={tone === 'primary' ? 'contained' : tone === 'secondary' ? 'outlined' : 'text'}
      {...rest}
      sx={[
        {
          minHeight: Math.max(spec.minHeight, 44),
          px: `${tone === 'quiet' ? 8 : spec.paddingX}px`,
          borderRadius: `${radius.md}px`,
          fontSize: spec.fontSize,
          fontWeight: fontWeightTokens.medium,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          transition: transition('background-color', 'border-color', 'color'),
          ...reducedMotionSx,
          ...focusRingSx,
          ...(tone === 'primary'
            ? {
                backgroundColor: hubColors.interactivePrimary,
                color: hubColors.textOnPrimary,
                '@media (hover: hover)': { '&:hover': { backgroundColor: hubColors.primaryHover } },
                '&.Mui-disabled': { backgroundColor: hubColors.surfaceSunken, color: hubColors.textMuted },
              }
            : tone === 'secondary'
              ? {
                  borderColor: hubColors.borderStrong,
                  color: hubColors.textPrimary,
                  backgroundColor: 'transparent',
                  '@media (hover: hover)': { '&:hover': { borderColor: hubColors.textSecondary, backgroundColor: hubColors.interactiveHover } },
                }
              : {
                  color: hubColors.link,
                  '@media (hover: hover)': { '&:hover': { backgroundColor: hubColors.interactiveHover } },
                }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Button>
  );
}

export type ChoiceOption = { value: string; label: string };

/**
 * Pill-free choice chips at radius 12 inside a wrapping row. `multiple` makes
 * it a checkbox group; otherwise a radio group. Every chip keeps 44px.
 */
export function ChoiceChips({
  options,
  value,
  onChange,
  multiple = false,
  labelledBy,
  invalid,
}: {
  options: ChoiceOption[];
  value: string | string[] | null | undefined;
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  labelledBy?: string;
  invalid?: boolean;
}) {
  const selected = new Set(Array.isArray(value) ? value : value ? [value] : []);
  return (
    <Box role={multiple ? 'group' : 'radiogroup'} aria-labelledby={labelledBy} aria-invalid={invalid || undefined} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {options.map((o) => {
        const on = selected.has(o.value);
        return (
          <ButtonBase
            key={o.value}
            role={multiple ? 'checkbox' : 'radio'}
            aria-checked={on}
            onClick={() => {
              if (!multiple) return onChange(o.value);
              const next = new Set(selected);
              if (on) next.delete(o.value);
              else next.add(o.value);
              onChange(options.map((x) => x.value).filter((v) => next.has(v)));
            }}
            sx={{
              minHeight: 44,
              px: 4,
              borderRadius: `${radius.sm}px`,
              border: `1px solid ${on ? hubColors.interactivePrimary : invalid ? hubColors.statusError : hubColors.borderStrong}`,
              backgroundColor: on ? hubColors.chip : 'transparent',
              color: on ? hubColors.chipText : hubColors.textPrimary,
              fontSize: 14.5,
              fontWeight: fontWeightTokens.medium,
              gap: 1.5,
              transition: transition('background-color', 'border-color', 'color'),
              ...reducedMotionSx,
              ...focusRingSx,
              '@media (hover: hover)': { '&:hover': { borderColor: hubColors.interactivePrimary } },
            }}
          >
            {on ? <CheckIcon size={14} /> : null}
            {o.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

export function WhyLine({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <Box component="p" id={id} sx={{ m: 0, fontSize: 13, lineHeight: 1.45, color: hubColors.textMuted }}>
      {children}
    </Box>
  );
}

export function FieldError({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <Box component="p" id={id} role="alert" sx={{ m: 0, fontSize: 13, lineHeight: 1.45, color: hubColors.statusError }}>
      {children}
    </Box>
  );
}

/** Flat content panel: surface, hairline, radius 20. Never glass. */
export function Panel({ children, sx, component = 'section', ...rest }: { children: ReactNode; sx?: object; component?: 'section' | 'div' | 'article' } & Record<string, unknown>) {
  return (
    <Box component={component} {...rest} sx={{ backgroundColor: hubColors.surfaceCard, border: hairline, borderRadius: `${radius.lg}px`, p: { xs: 4, md: 5 }, ...sx }}>
      {children}
    </Box>
  );
}

export type DotTone = 'muted' | 'info' | 'warning' | 'success' | 'primary' | 'error';

export const DOT_COLOR: Record<DotTone, string> = {
  muted: hubColors.textMuted,
  info: hubColors.statusInfo,
  warning: hubColors.statusWarning,
  success: hubColors.statusSuccess,
  primary: hubColors.interactivePrimary,
  error: hubColors.statusError,
};

/** Status pill: 28px, hairline, 7px dot in the tone colour; text stays in primary ink. */
export function StatusPill({ tone, children }: { tone: DotTone; children: ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 2, height: 28, px: 3, borderRadius: `${radius.pill}px`, border: hairline, fontSize: 13, fontWeight: fontWeightTokens.medium, color: hubColors.textPrimary, whiteSpace: 'nowrap' }}>
      <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: DOT_COLOR[tone] }} />
      {children}
    </Box>
  );
}

type IconProps = { size?: number };
const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

export const CheckIcon = ({ size = 16 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...stroke}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);
export const CloseIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...stroke}><path d="M6 6l12 12M18 6L6 18" /></svg>
);
export const WarnIcon = ({ size = 16 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...stroke}><path d="M12 4l9 16H3z" /><path d="M12 10v4M12 17.2v.3" /></svg>
);
export const UploadIcon = ({ size = 22 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...stroke}><path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" /></svg>
);
export const HomeIcon = ({ size = 20 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...stroke}><path d="M4 11l8-7 8 7v9H4z" /><path d="M10 20v-5h4v5" /></svg>
);
export const TrashIcon = ({ size = 16 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...stroke}><path d="M5 7h14M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg>
);
export const ExternalIcon = ({ size = 16 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...stroke}><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>
);

/** The token gradient used wherever a listing photo is missing. */
export const photoFallback = `linear-gradient(135deg, color-mix(in srgb, ${hubColors.backgroundSecondary} 75%, transparent) 0%, color-mix(in srgb, ${hubColors.interactiveSecondary} 15%, transparent) 100%)`;

export { formatDay, formatRent } from './format';
